import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  MenuList,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { green, red } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowBack, CheckCircle, Error } from "@material-ui/icons";
import { motion } from "framer-motion";
import { isEmpty } from "lodash";
import { stringify } from "query-string";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Lottie from "react-lottie-player";
import { useHistory, useLocation } from "react-router-dom";
import { JsonParam, StringParam } from "serialize-query-params";
import corporateHealthService from "services/corporate-health.service";
import { BooleanParam, useQueryParam } from "use-query-params";
import { mapRegionNameToCode } from "util/address/fetchAddressComponents";
import { processHealthPass } from "util/ProcessHealthPass";
import { slideRight, slideUp } from "util/transitions";
import { useStyles as baseCardStyles } from "../MedicalId/index";
import animationData from "./anim_completion.json";
import failAnimationData from "./fail.json";

const useStyles = makeStyles((theme) => ({
  centered: {
    justifyContent: "center",
    backgroundColor: theme.palette.background.card,
  },
  errorIcon: {
    color: theme.palette.getContrastText(red[500]),
    backgroundColor: red[500],
  },
  successIcon: {
    color: theme.palette.getContrastText(green[700]),
    backgroundColor: green[500],
  },
  lottie: {
    maxWidth: 415,
    marginLeft: "auto",
    marginRight: "auto",
  },
}));

function LinkMenuItem({ loading, isError, linkUrl, linkLabel, linkParams }) {
  const history = useHistory();
  const classes = useStyles();

  return (
    <MenuItem
      style={{ whiteSpace: "normal" }}
      onClick={() => {
        history.push({
          pathname: linkUrl,
          search: linkParams,
        });
      }}
      disableGutters
    >
      {!loading ? (
        <ListItemAvatar>
          {isError ? (
            <Avatar className={classes.errorIcon}>
              <Error />
            </Avatar>
          ) : (
            <Avatar className={classes.successIcon}>
              <ArrowBack />
            </Avatar>
          )}
        </ListItemAvatar>
      ) : (
        <ListItemAvatar>
          <Avatar className={classes.successIcon}>
            <ArrowBack />
          </Avatar>
        </ListItemAvatar>
      )}
      <ListItemText
        primary={`${isError ? "Error in " : "Review "} ${linkLabel}`}
      />
    </MenuItem>
  );
}

export default function Finalize() {
  const classes = { ...useStyles(), ...baseCardStyles() };
  const history = useHistory();
  const location = useLocation();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"), {
    noSsr: true,
  });
  const [linkIdForm, setLinkIdForm] = useQueryParam("linkIdForm", JsonParam);
  const [addressFormValues, setAddressFormValues] = useQueryParam(
    "addressInfoForm",
    JsonParam
  );
  const [additionalInfoForm, setAdditionalInfoForm] = useQueryParam(
    "additionalInfo",
    JsonParam
  );
  const [medicalId, setMedicalId] = useQueryParam(
    "medicalFormValues",
    JsonParam
  );

  const [dependentForm, setDependentForm] = useQueryParam(
    "dependentForm",
    JsonParam
  );

  const [isDependent, setIsDependent] = useState(false);

  const [hashId, setHashId] = useQueryParam("code", StringParam);

  const [hash, setHash] = useState();
  const [token, setToken] = useState();

  const [startLottieAnim, setStartLottieAnim] = useState(false);
  const [gotoEndAnim, setGotoEndAnim] = useState(0);
  const [loading, setLoading] = useState(false);

  const [deepLink, setDeepLink] = useState("");

  useEffect(() => {
    async function fetchHealthPass() {
      const healthPass = await processHealthPass(hashId);
      setIsDependent(healthPass?.dependentDetails?.isDependent);
    }
    fetchHealthPass();
  }, [hashId]);

  const processUpdateEmployee = useCallback(async () => {
    if (!hashId) {
      return
    }
    try {
      const { data: inviteParams } =
        await corporateHealthService.getHealthPassFromCode(hashId);

      const comorbidities = additionalInfoForm?.comorbidities;
      const allergies = additionalInfoForm?.allergies;
      const missingComorbidity = (
        inviteParams?.employee_reference?.comorbidities ?? []
      )
        .filter((c) => !comorbidities.includes(c))
        .map((c) => {
          return { ...c, id: c.id, has_comorbidity: false };
        });
      const mappedComorbidity = comorbidities.map(({ label, ...c }) => {
        return { ...c, id: c.id, has_comorbidity: true };
      });

      const missingAllergy = inviteParams?.employee_reference?.allergies
        .filter((c) => !allergies.includes(c))
        .map((c) => {
          return { ...c, id: c.id, has_allergy: false };
        });
      const mappedAllergy = allergies.map(({ label, type, ...c }) => {
        return {
          ...c,
          id: c.id,

          has_allergy: true,
        };
      });

      const doses = [
        ...(additionalInfoForm?.hasFirstDose === "true"
          ? [additionalInfoForm?.firstDoseDate]
          : []),
        ...(additionalInfoForm?.hasSecondDose === "true"
          ? [additionalInfoForm?.secondDoseDate]
          : []),
      ].map((dose, idx) => {
        return {
          date: dose,
          number: idx + 1,
        };
      });

      const region = await mapRegionNameToCode(addressFormValues?.region);

      const dependentsParams = isDependent?{
        primary_employee_company_id: dependentForm?.primaryEmployeeId,
        relationship:dependentForm?.dependentRelationship,
      }:{}

      const newParams = {
        ...dependentsParams,
        company_id: inviteParams?.employee_reference?.company_id,
        first_name: medicalId?.firstName,
        middle_name: medicalId?.middleName,
        last_name: medicalId?.lastName,
        email: inviteParams?.employee_reference?.email,
        mobile_number: inviteParams?.employee_reference?.mobile_number,
        gender: medicalId?.gender,
        birth_date: medicalId?.birthDate,
        role: inviteParams?.employee_reference?.role?.label,
        seniority: inviteParams?.employee_reference?.seniority?.label,
        civil_status: addressFormValues?.civilStatus,

        province: addressFormValues?.province,
        city: addressFormValues?.city,
        street: addressFormValues?.street,
        building_info: addressFormValues?.building_info,
        barangay: addressFormValues?.barangay,
        region: region,

        department: inviteParams?.employee_reference?.department?.label,

        comorbidities: [...mappedComorbidity, ...missingComorbidity],
        allergies: [...missingAllergy, ...mappedAllergy],
        vaccines: [
          ...(additionalInfoForm?.hasFirstDose === "true"
            ? [
                {
                  vaccine: additionalInfoForm?.vaccine?.name,
                  doses: doses,
                },
              ]
            : []),
        ],
      };

      //todo: 500: invite accepted error
      const result = await corporateHealthService.acceptInvite(
        hashId,
        newParams, 
      );
      setHash(result?.data?.hash_id);
      setToken(result?.data?.token);
      setDeepLink(result?.data?.app_deep_link);
      // const passDetails = await corporateHealthService.getCorporatePass(
      //   result?.data?.hash_id,
      //   inviteParams?.employee_reference?.affiliation?.id,
      //   result?.data?.token
      // );
      // console.log("📢[index.js:181]:", passDetails);
      history.push({
        pathname: "/onboarding/healthPass",
        search: stringify({
          hash_id: result?.data?.hash_id,
          code: hashId,
          token: result?.data?.token,
          deep_link: result?.data?.app_deep_link,
        }),
        state: {
          inviteParams,
          deepLink,
        },
      });
    } catch (e) {
      console.log("📢[index.js:208]:", e);
    }
  }, [additionalInfoForm?.allergies, additionalInfoForm?.comorbidities, additionalInfoForm?.firstDoseDate, additionalInfoForm?.hasFirstDose, additionalInfoForm?.hasSecondDose, additionalInfoForm?.secondDoseDate, additionalInfoForm?.vaccine?.name, addressFormValues?.barangay, addressFormValues?.city, addressFormValues?.civilStatus, addressFormValues?.province, addressFormValues?.region, deepLink, dependentForm?.dependentRelationship, dependentForm?.primaryEmployeeId, hashId, history, isDependent, medicalId?.birthDate, medicalId?.firstName, medicalId?.gender, medicalId?.lastName, medicalId?.middleName]);

  const isValidLinkIdForm = useMemo(() => {
    return !isEmpty(linkIdForm);
  }, [linkIdForm]);

  const isValidAddressFormValues = useMemo(() => {
    return !isEmpty(addressFormValues);
  }, [addressFormValues]);

  const isValidAdditionalInfoForm = useMemo(() => {
    return !isEmpty(additionalInfoForm);
  }, [additionalInfoForm]);

  const isValidMedicalId = useMemo(() => {
    return !isEmpty(medicalId);
  }, [medicalId]);

  const isValidDependentForm = useMemo(() => {
    return !isDependent ? true : !isEmpty(dependentForm);
  }, [dependentForm, isDependent]);

  const failForm = useMemo(() => {
    return !(
      isValidAdditionalInfoForm &&
      isValidLinkIdForm &&
      isValidMedicalId &&
      isValidAddressFormValues &&
      isValidDependentForm
    );
  }, [
    isValidAdditionalInfoForm,
    isValidAddressFormValues,
    isValidDependentForm,
    isValidLinkIdForm,
    isValidMedicalId,
  ]);

  async function submitForm() {
    setLoading(true);
    await processUpdateEmployee();
    setLoading(false);
  }
  return (
    <motion.div
      {...(isMobile ? slideRight : slideUp)}
      onAnimationComplete={(def) => setStartLottieAnim(true)}
    >
      <Container fixed maxWidth="md">
        <Card>
          <CardContent>
            {!failForm ? (
              <>
                <Typography align="center" variant="h2">
                  Success!
                </Typography>
                <Lottie
                  className={classes.lottie}
                  play={startLottieAnim}
                  loop={0}
                  animationData={animationData}
                />
                <Typography align="center">
                  Click proceed to access your QR code and to save your employee
                  record{" "}
                </Typography>
              </>
            ) : (
              <>
                <Typography align="center" variant="h2">
                  Missing/Invalid info
                </Typography>
                <Lottie
                  className={classes.lottie}
                  play={startLottieAnim}
                  loop={0}
                  animationData={failAnimationData}
                />
                <Typography align="center">
                  Click to fill missing / review form data
                </Typography>
              </>
            )}
          </CardContent>
          <CardContent>
            <MenuList>
              <LinkMenuItem
                isError={!isValidMedicalId}
                loading={loading}
                linkUrl="/onboarding/medical"
                linkLabel="Medical ID"
                linkParams={location.search}
              />
              <LinkMenuItem
                isError={!isValidLinkIdForm}
                loading={loading}
                linkUrl="/onboarding/linkid"
                linkLabel="Corporate ID Linking"
                linkParams={location.search}
              />
              {isDependent && (
                <LinkMenuItem
                  isError={!isValidDependentForm}
                  loading={loading}
                  linkUrl="/onboarding/dependent"
                  linkLabel="Dependent Relationship"
                  linkParams={location.search}
                />
              )}
              <LinkMenuItem
                isError={!isValidAddressFormValues}
                loading={loading}
                linkUrl="/onboarding/basicinfo"
                linkLabel="Additional Employee Information"
                linkParams={location.search}
              />
              <LinkMenuItem
                isError={!isValidAdditionalInfoForm}
                loading={loading}
                linkUrl="/onboarding/medical2"
                linkLabel="Additional Medical Information"
                linkParams={location.search}
              />
            </MenuList>
          </CardContent>
          {!failForm ? (
            <CardActions className={classes.centered}>
              <Button
                variant="contained"
                color="primary"
                disabled={loading}
                onClick={() => submitForm()}
              >
                {loading && <CircularProgress size={20} color="primary" />}
                Proceed
              </Button>
            </CardActions>
          ) : (
            <></>
          )}
        </Card>
        {/* <Card>
          <CardContent>
            <ReactJson
              src={{
                hashId,
                linkIdForm: linkIdForm ?? {},
                addressFormValues: addressFormValues ?? {},
                additionalInfoForm: additionalInfoForm ?? {},
                medicalId: medicalId ?? {},
              }}
            />
          </CardContent>
        </Card> */}
      </Container>
    </motion.div>
  );
}
